import axios from "axios";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import i18n from "i18next";

// API Headers

export const API_REQUEST_HEADERS = {
  "Content-Type": "application/json",
};

export const API_REQUEST_HEADERS_BASEURL = axios.create({
  baseURL: process.env.REACT_APP_API_FLEET_URL || "/api",
  headers: {
    "Content-Type": "application/json",
  },
});

export async function getFirebaseIdToken({ forceRefresh = false } = {}) {
  let currentUser = auth.currentUser;

  if (!currentUser) {
    currentUser = await new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        (user) => {
          unsubscribe();
          resolve(user || null);
        },
        () => {
          unsubscribe();
          resolve(null);
        }
      );
    });
  }

  if (!currentUser) return "";
  return currentUser.getIdToken(forceRefresh);
}

API_REQUEST_HEADERS_BASEURL.interceptors.request.use(async (config) => {
  const token = await getFirebaseIdToken();
  config.headers = config.headers || {};
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if ("Authorization" in config.headers) {
    delete config.headers.Authorization;
  }
  return config;
});

API_REQUEST_HEADERS_BASEURL.interceptors.response.use((response) => response, (error) => {
  const data = error?.response?.data;
  if (data?.code === "FLEET_PLAN_REQUIRED") {
    data.message = i18n.t("billing.operations.required");
    const fleetId = error.config?.url?.match(/\/fleets\/([^/]+)/)?.[1];
    window.dispatchEvent(new CustomEvent("fleet-plan-blocked", { detail: { fleetId, access: data.access } }));
  }
  return Promise.reject(error);
});

// Direct Firestore mutations also check billing for a useful error; rules enforce it independently.
export async function assertFleetOperationsAllowed(fleetId) {
  const { data: access } = await API_REQUEST_HEADERS_BASEURL.get(`/fleets/${fleetId}/operations-access`);
  if (access.active) return;
  window.dispatchEvent(new CustomEvent("fleet-plan-blocked", { detail: { fleetId, access } }));
  const error = new Error(i18n.t("billing.operations.required"));
  error.response = { status: 402, data: { code: "FLEET_PLAN_REQUIRED", messageKey: "billing.operations.required", message: error.message } };
  throw error;
}

export const GOOGLE_MAP_LIBRARIES = ["places"];

// API Endpoints
// Deprecated for fleet profile details (use Firestore `fleets/{fleetId}` reads/writes instead):
export const API_FleetDrivers_update_details = `/fleets/{fleetId}`;
export const API_FleetDrivers_sign_up = `/signup`;
export const API_FleetDrivers = `/fleets/{fleetId}/drivers`;
export const API_FleetDrivers_ID_delete_drivers = `/fleets/{fleetId}/driver-to-remove/{userId}`;
export const API_FleetDrivers_ID_get_details = `/fleets/{fleetId}/drivers/{userId}/details`;
export const API_FleetDriver_ID_images_and_documents = `/drivers/{userId}/files`;
export const API_FleetDriver_ID_post_cards = `/fleets/{fleetId}/transportOrder`;
export const API_FleetTransportOrderDrafts_extract = `/fleets/{fleetId}/transportOrderDrafts/extract`;
export const API_FleetTransportOrderDrafts = `/fleets/{fleetId}/transportOrderDrafts`;
export const API_FleetTransportOrderDrafts_processReady = `/fleets/{fleetId}/transportOrderDrafts/process-ready`;
export const API_FleetTransportOrderDrafts_settings = `/fleets/{fleetId}/transportOrderDrafts/settings`;
export const API_FleetTransportOrderDraft = `/fleets/{fleetId}/transportOrderDrafts/{draftId}`;
export const API_FleetTransportOrderDraft_review = `/fleets/{fleetId}/transportOrderDrafts/{draftId}/review`;
export const API_FleetTransportOrderDraft_reprocess = `/fleets/{fleetId}/transportOrderDrafts/{draftId}/reprocess`;
export const API_FleetTransportOrderDraft_directoryResolution = `/fleets/{fleetId}/transportOrderDrafts/{draftId}/directory-resolution/{field}`;
export const API_FleetTransportOrderDraft_checkpointResolution = `/fleets/{fleetId}/transportOrderDrafts/{draftId}/directory-resolution/checkpoints/{checkpointIndex}`;
export const API_FleetOrderEmailAlias = `/fleets/{fleetId}/order-email-alias`;
export const API_FleetDriver_ID_get_cards_OT = `/fleets/{fleetId}/transportOrders`;
export const API_FleetDriver_ID_search_cards_OT = `/fleets/{fleetId}/transportOrders/search`;
export const API_FleetDriver_ID_get_onecard_OT = `/fleets/{fleetId}/transportOrder/{fleetOT}`;
export const API_FleetDriver_ID_patch_cards_OT = `/fleets/{fleetId}/transportOrder/{fleetOT}`;
export const API_FleetDriver_ID_download_edoc_OT = `/fleets/{fleetId}/transportOrder/{fleetOT}/edoc.pdf`;
export const API_FleetDriver_ID_finalize_OT = `/fleets/{fleetId}/transportOrder/{transportOrderId}/finalize`;
export const API_FleetDriver_ID_patch_status_OT = `/fleets/{fleetId}/transportOrder-status/{transportOrderId}`;
export const API_FleetDriver_ID_patch_status_available_OT = `/fleets/{fleetId}/transportOrder-status-available/{transportOrderId}`;
export const API_FleetDriver_ID_post_assign_OT = `/fleets/{fleetId}/transportOrder/{transportOrderId}/assign`;
export const API_FleetDriver_ID_post_unassign_OT = `/fleets/{fleetId}/transportOrder/{transportOrderId}/unassign`;
export const API_FleetDriver_ID_delete_cards_OT = `/fleets/{fleetId}/transportOrder/{fleetOT}`;
export const API_Fleet_ID_get_details = "/fleets/{fleetId}";
export const API_Fleet_ID_archive = "/fleets/{fleetId}/archive-orders";
export const API_Fleet_ID_planned_route =
  "/fleets/{fleetId}/transportOrder/{transportOrderId}/planned-route";
export const API_Fleet_ID_planned_route_preview = "/fleets/{fleetId}/planned-route-preview";
export const API_FleetAssistant_chat = "/fleets/{fleetId}/assistant/chat";
export const API_FleetAssistant_history = "/fleets/{fleetId}/assistant/history";
export const API_FleetAssistant_dashboard_customization =
  "/fleets/{fleetId}/assistant/customizations/dashboard";
export const API_FleetConversation_post = "/fleets/{fleetId}/conversation";
export const API_FleetDirectory_partners = "/fleets/{fleetId}/directory/partners";
export const API_FleetDirectory_locations = "/fleets/{fleetId}/directory/locations";
export const API_FleetDirectory_shared_search = "/fleets/{fleetId}/directory/shared-search";
export const API_FleetConversation_massive = "/fleets/{fleetId}/conversations/massive";
//Books
export const API_Fleet_ID_book = "/fleets/{fleetId}/library";
export const API_Fleet_ID_deleteBook = "/fleets/{fleetId}/library/{bookId}";

//Parking
export const API_Fleet_ID_parking_config = "/fleets/{fleetId}/parking/config";
export const API_Fleet_ID_parking_areas = "/fleets/{fleetId}/parking/areas";
export const API_Fleet_ID_parking_bookings = "/fleets/{fleetId}/parking/bookings";
export const API_Fleet_ID_parking_booking = "/fleets/{fleetId}/parking/bookings/{bookingId}";

//Incidents
export const API_FleetDriver_ID_get_incidents = `/fleets/{fleetId}/incidents-journey`;
export const API_FleetDriver_ID_get_oneincident = `/fleets/{fleetId}/incidents-journey/{incidentId}`;
export const API_FleetDriver_ID_patch_oneincident = `/fleets/{fleetId}/incidents-journey/{incidentId}`;

//Public Order
export const API_PublicLink_post = `/publicLink/fleets/{fleetId}/transport-order/{transportOrderId}`;
export const API_PublicLink_get = `/publicLink/fleets/{fleetId}/transportOrderId/{transportOrderId}/{token}`;
export const API_PublicLink_patch = `/publicLink/fleets/{fleetId}/transport-order/{transportOrderId}/public-link`;
